import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, Button, Select, DatePicker } from 'antd';
import ModalDlg from 'components/Basic/ModalDlg';
import { Close, ArrowForward, ArrowDropDown, ContactSupport } from '@material-ui/icons';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import toast from 'components/Basic/Toast';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import {
  useSaveParticipationMutation,
  useLazyValidateParticipationCodeQuery,
  useLazyValidatePhoneQuery,
  useLazyGetActiveCampaignsQuery,
  useLazyGetDocumentTypesQuery,
} from 'core/worldCup/customerSupport/CustomerSupportService';
import { ValidatePhoneParticipant } from 'core/worldCup/customerSupport/types';
import { sanitizeRule } from 'utilities/sanitize';
import { setVerifiedCustomer, clearVerifiedCustomer, setValidatedCode, clearValidatedCode, setDocumentTypes, setCampaigns, setSelectedCampaignId } from 'core/worldCup/customerSupport/CustomerSupportSlice';
import {
  useLazyGetDepartmentsQuery,
} from 'core/region/RegionService';
import { setDepartmentsData } from 'core/region/RegionSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';
dayjs.extend(utc);

type CustomerSupportModalProps = {
  mode: 'modal' | 'fullscreen';
  isOpenModal?: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
};

type ParticipationFormData = {
  firstName: string;
  lastName: string;
  email?: string;
  dob: any;
  identificationTypeId: string;
  identificationNumber: string;
  state: string;
  hasVisa: boolean;
  address: string;
};

type StepType = 'campaign' | 'code' | 'verify' | 'participation';

const CODE_LENGTH = 6;

const CustomerSupportModal: React.FC<CustomerSupportModalProps> = ({
  mode,
  isOpenModal,
  onClose,
  onSaveSuccess,
}) => {
  const [participationForm] = Form.useForm<ParticipationFormData>();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isModal = mode === 'modal';

  const [step, setStep] = useState<StepType>('code');
  const [codeValues, setCodeValues] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [codeError, setCodeError] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [customerFound, setCustomerFound] = useState<boolean | null>(null);
  const [phoneResults, setPhoneResults] = useState<ValidatePhoneParticipant[]>([]);

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { departments } = useAppSelector(state => state.regionReducer);
  const verifiedCustomer = useAppSelector(state => state.customerSupportReducer?.verifiedCustomer ?? null);
  const validatedCode = useAppSelector(state => state.customerSupportReducer?.validatedCode ?? null);
  const adminId = useAppSelector(state => state.loginReducer?.admin?.id ?? 0);

  const [validateCode, { isFetching: isValidating }] = useLazyValidateParticipationCodeQuery();
  const [validatePhone, { isFetching: isChecking }] = useLazyValidatePhoneQuery();
  const [saveParticipation, { isLoading: isSaving }] = useSaveParticipationMutation();
  const [getActiveCampaigns, { isFetching: isLoadingCampaigns }] = useLazyGetActiveCampaignsQuery();
  const [getDepartments] = useLazyGetDepartmentsQuery();
  const [getDocumentTypes] = useLazyGetDocumentTypesQuery();

  const { documentTypes, campaigns = [], selectedCampaignId = null } = useAppSelector(state => state.customerSupportReducer);

  useEffect(() => {
    if (isOpenModal) {
      setCodeValues(Array(CODE_LENGTH).fill(''));
      setCodeError('');
      setPhoneValue('');
      setPhoneError('');
      setCustomerFound(null);
      setPhoneResults([]);
      participationForm.resetFields();
      dispatch(clearVerifiedCustomer());
      dispatch(clearValidatedCode());
      dispatch(setSelectedCampaignId(null));
      loadDepartments();
      loadDocumentTypes();
      loadCampaigns();
    }
  }, [isOpenModal]);

  useEffect(() => {
    if (isOpenModal && step === 'code') {
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    }
  }, [isOpenModal, step]);

  const loadDepartments = async () => {
    try {
      const res: any = await getDepartments({} as any).unwrap();
      dispatch(setDepartmentsData({ departments: res.rows }));
    } catch (e) {
      console.error('Error cargando departamentos:', e);
    }
  };

  const loadDocumentTypes = async () => {
    try {
      const countryCode = process.env.REACT_APP_COUNTRY || 'gt';
      const res: any = await getDocumentTypes({ countryCode }).unwrap();
      dispatch(setDocumentTypes(res));
    } catch (e) {
      console.error('Error cargando tipos de documento:', e);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res: any = await getActiveCampaigns().unwrap();
      const list = Array.isArray(res) ? res : res?.data ?? [];
      dispatch(setCampaigns(list));

      if (list.length === 1) {
        dispatch(setSelectedCampaignId(list[0].id));
        setStep('code');
      } else if (list.length > 1) {
        setStep('campaign');
      } else {
        setStep('code');
      }
    } catch (e) {
      console.error('Error cargando campañas activas:', e);
      setStep('code');
    }
  };

  const handleCodeChange = useCallback((index: number, value: string) => {
    const cleaned = value.replace(/[^A-Z0-9]/g, '');
    if (!cleaned && !value) {
      const newValues = [...codeValues];
      newValues[index] = '';
      setCodeValues(newValues);
      return;
    }

    if (cleaned.length === 1) {
      const newValues = [...codeValues];
      newValues[index] = cleaned;
      setCodeValues(newValues);
      setCodeError('');
      if (index < CODE_LENGTH - 1) {
        codeInputRefs.current[index + 1]?.focus();
      }
    } else if (cleaned.length > 1) {
      const newValues = [...codeValues];
      const chars = cleaned.slice(0, CODE_LENGTH).split('');
      chars.forEach((char, i) => {
        if (index + i < CODE_LENGTH) {
          newValues[index + i] = char;
        }
      });
      setCodeValues(newValues);
      setCodeError('');
      const lastIndex = Math.min(index + chars.length, CODE_LENGTH) - 1;
      codeInputRefs.current[lastIndex]?.focus();
    }
  }, [codeValues]);

  const handleCodeKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (codeValues[index]) {
        const newValues = [...codeValues];
        newValues[index] = '';
        setCodeValues(newValues);
      } else if (index > 0) {
        const newValues = [...codeValues];
        newValues[index - 1] = '';
        setCodeValues(newValues);
        codeInputRefs.current[index - 1]?.focus();
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus();
      e.preventDefault();
    } else if (e.key === 'Enter') {
      handleValidateCode();
    }
  }, [codeValues]);

  const handleCodePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/[^A-Z0-9]/g, '');
    if (!pasteData) return;
    const chars = pasteData.slice(0, CODE_LENGTH).split('');
    const newValues = Array(CODE_LENGTH).fill('');
    chars.forEach((char, i) => {
      newValues[i] = char;
    });
    setCodeValues(newValues);
    setCodeError('');
    const lastIndex = Math.min(chars.length, CODE_LENGTH) - 1;
    codeInputRefs.current[lastIndex]?.focus();
  }, []);

  const handleBackToCode = () => {
    setStep(campaigns.length > 1 ? 'campaign' : 'code');
    setCodeValues(Array(CODE_LENGTH).fill(''));
    setCodeError('');
    setPhoneValue('');
    setPhoneError('');
    setCustomerFound(null);
    setPhoneResults([]);
    participationForm.resetFields();
    dispatch(clearVerifiedCustomer());
    dispatch(clearValidatedCode());
    if (campaigns.length > 1) {
      dispatch(setSelectedCampaignId(null));
    }
  };

  const handleValidateCode = async () => {
    const code = codeValues.join('');
    if (code.length < CODE_LENGTH) {
      setCodeError(intl.formatMessage({ id: 'Complete code is required' }));
      return;
    }
    setCodeError('');
    try {
      await validateCode({ campaignId: String(selectedCampaignId), code }).unwrap();
      dispatch(setValidatedCode(code));
      setStep('verify');
    } catch (e: any) {
      console.error('Error validando código:', e);
      const defaultMsg = intl.formatMessage({ id: 'Invalid participation code' });
      setCodeError(getApiErrorMessage(e, intl.locale, defaultMsg));
    }
  };

  const applyParticipantData = (participant: ValidatePhoneParticipant) => {
    const nameParts = (participant.fullName || '').trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    dispatch(setVerifiedCustomer({
      customer: {
        id: participant.userId,
        firstName,
        lastName,
        phone: phoneValue,
        email: participant.email || '',
        dob: participant.birthDate || '',
        identificationNumber: participant.participantIdNumber || '',
        hasVisa: participant.hasAmericanVisa ?? false,
        address: participant.address || '',
      },
    }));

    participationForm.setFieldsValue({
      firstName,
      lastName,
      email: participant.email || '',
      dob: participant.birthDate ? dayjs.utc(participant.birthDate) : undefined,
      identificationTypeId: participant.participantIdType || undefined,
      identificationNumber: participant.participantIdNumber || '',
      state: participant.state || undefined,
      hasVisa: participant.hasAmericanVisa ?? false,
      address: participant.address || '',
    });

    setCustomerFound(true);
    setPhoneResults([]);
    setStep('participation');
  };

  const applyEmptyCustomer = () => {
    dispatch(setVerifiedCustomer({
      customer: { id: 0, firstName: '', lastName: '', phone: phoneValue },
    }));
    setCustomerFound(false);
    setPhoneResults([]);
    setStep('participation');
  };

  const handleSelectParticipant = (participant: ValidatePhoneParticipant) => {
    applyParticipantData(participant);
  };

  const handleVerifyCustomer = async () => {
    if (!phoneValue) {
      setPhoneError(intl.formatMessage({ id: 'Phone is required' }));
      return;
    }
    setPhoneError('');
    setPhoneResults([]);

    try {
      const res: any = await validatePhone({ campaignId: String(selectedCampaignId), phone: `+${phoneValue}` }).unwrap();
      const participants: ValidatePhoneParticipant[] = res?.data || [];

      if (participants.length === 1) {
        applyParticipantData(participants[0]);
      } else if (participants.length > 1) {
        setPhoneResults(participants);
      } else {
        applyEmptyCustomer();
      }
    } catch (e) {
      console.error('Error verificando teléfono:', e);
      applyEmptyCustomer();
    }
  };

  const handleSaveParticipation = async (values: ParticipationFormData) => {
    if (!verifiedCustomer || !validatedCode || !selectedCampaignId) return;

    try {
      await saveParticipation({
        campaignId: selectedCampaignId,
        fullName: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email || '',
        phone: `+${verifiedCustomer.phone}`,
        birthDate: values.dob ? dayjs(values.dob).format('YYYY-MM-DD') : '',
        participantIdType: values.identificationTypeId,
        participantIdNumber: values.identificationNumber,
        hasAmericanVisa: values.hasVisa,
        state: values.state,
        address: values.address,
        participationType: 'SELF',
        participationCode: validatedCode,
        userId: adminId,
      }).unwrap();

      toast.success({ title: intl.formatMessage({ id: 'Participation saved successfully' }) });
      onClose();
      onSaveSuccess();
    } catch (e: any) {
      console.error('Error guardando participación:', e);
      const defaultMsg = intl.formatMessage({ id: 'Error saving participation' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    }
  };

  const handleSelectCampaign = (campaignId: number) => {
    dispatch(setSelectedCampaignId(campaignId));
    setStep('code');
  };

  const selectClassName = isModal ? 'select_modal' : 'select_form';

  const renderCampaignStep = () => (
    <div className={isModal ? 'basic_info_form' : 'config_form'}>
      <div className="cs_code_step">
        <p className="cs_code_instruction">
          <FormattedMessage id="Select campaign" />
        </p>
        <Select
          className={selectClassName}
          suffixIcon={<ArrowDropDown />}
          placeholder={intl.formatMessage({ id: 'Select campaign' })}
          dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
          style={{ width: '100%', marginBottom: 16 }}
          onChange={(value: number) => handleSelectCampaign(value)}
          loading={isLoadingCampaigns}
        >
          {campaigns.map(c => (
            <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );

  const renderCodeStep = () => (
    <div className={isModal ? 'basic_info_form' : 'config_form'}>
      <div className="cs_code_step">
        <p className="cs_code_instruction">
          <FormattedMessage id="Enter participation code" />
        </p>
        <div className="cs_code_inputs" onPaste={handleCodePaste}>
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <input
              key={index}
              ref={el => { codeInputRefs.current[index] = el; }}
              className={`cs_code_input ${codeError ? 'has_error' : ''}`}
              type="text"
              maxLength={1}
              value={codeValues[index]}
              onChange={e => handleCodeChange(index, e.target.value)}
              onKeyDown={e => handleCodeKeyDown(index, e)}
              autoComplete="off"
            />
          ))}
        </div>
        {codeError && <div className="cs_code_error">{codeError}</div>}
        <Button
          className="send_code_btn"
          onClick={handleValidateCode}
          loading={isValidating}
          disabled={codeValues.join('').length < CODE_LENGTH}
        >
          <FormattedMessage id="Validate code" />
        </Button>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className={isModal ? 'basic_info_form' : 'config_form'}>
      <div className="cs_form_fields">
        <div className="cs_form_row phone_row">
          <div className="cs_field phone_field">
            <p className="title"><FormattedMessage id="Phone" /></p>
            <div className={`phone_wrapper ${phoneError ? 'has_error' : ''}`}>
              <ReactPhoneInput
                country="gt"
                value={phoneValue}
                onChange={(value: string) => {
                  setPhoneValue(value);
                  setPhoneError('');
                  setPhoneResults([]);
                }}
              />
              {phoneError && <div className="phone_error">{phoneError}</div>}
            </div>
          </div>
          <div className="cs_field send_code_btn_field">
            <Button
              className="send_code_btn"
              onClick={handleVerifyCustomer}
              loading={isChecking}
            >
              <FormattedMessage id="Verify customer" />
            </Button>
            <Button className="back_btn" onClick={handleBackToCode}>
              <FormattedMessage id="Cancel" />
            </Button>
          </div>
        </div>
        {phoneResults.length > 1 && (
          <div className="cs_phone_results">
            <p className="cs_phone_results_title">
              <FormattedMessage id="Multiple participants found" /> ({phoneResults.length})
            </p>
            <div className="cs_phone_results_list">
              {phoneResults.map((p, idx) => (
                <div
                  key={idx}
                  className="cs_phone_result_item"
                  onClick={() => handleSelectParticipant(p)}
                >
                  <div className="cs_phone_result_name">{p.fullName}</div>
                  <div className="cs_phone_result_details">
                    <span>{p.participantIdType}: {p.participantIdNumber}</span>
                    {p.email && <span>{p.email}</span>}
                  </div>
                </div>
              ))}
            </div>
            <Button className="cs_phone_results_skip" onClick={applyEmptyCustomer}>
              <FormattedMessage id="Continue without selecting" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderParticipationStep = () => (
    <Form
      form={participationForm}
      className={isModal ? 'basic_info_form' : 'config_form'}
      onFinish={handleSaveParticipation}
    >
      {customerFound !== null && (
        <div className={`cs_info_message ${customerFound ? 'success' : 'warning'}`}>
          <FormattedMessage id={customerFound ? 'Customer found message' : 'Customer not found message'} />
        </div>
      )}

      <div className="cs_form_fields">
        <div className="cs_form_row two_columns">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="First name" /></p>
            <Form.Item name="firstName" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input placeholder={intl.formatMessage({ id: 'First name' })} />
            </Form.Item>
          </div>
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Last name" /></p>
            <Form.Item name="lastName" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input placeholder={intl.formatMessage({ id: 'Last name' })} />
            </Form.Item>
          </div>
        </div>

        <div className="cs_form_row two_columns">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Full name" /></p>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.firstName !== cur.firstName || prev.lastName !== cur.lastName}>
              {() => (
                <Input
                  disabled placeholder={intl.formatMessage({ id: 'Full name' })}
                  value={`${participationForm.getFieldValue('firstName') || ''} ${participationForm.getFieldValue('lastName') || ''}`.trim()}
                />
              )}
            </Form.Item>
          </div>
        </div>

        <div className="cs_form_row two_columns">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Email" /></p>
            <Form.Item name="email" rules={[sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input placeholder={intl.formatMessage({ id: 'Email' })} />
            </Form.Item>
          </div>
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Date of birth" /></p>
            <Form.Item name="dob" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
              <DatePicker
                className="cs_datepicker"
                placeholder={intl.formatMessage({ id: 'Date of birth' })}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </div>
        </div>

        <div className="cs_form_row two_columns">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Identification type" /></p>
            <Form.Item name="identificationTypeId" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
              <Select
                className={selectClassName}
                suffixIcon={<ArrowDropDown />}
                placeholder={intl.formatMessage({ id: 'Select identification type' })}
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                {documentTypes.map(type => (
                  <Select.Option key={type.id} value={type.key}>{type.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Identification number" /></p>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.identificationTypeId !== cur.identificationTypeId}>
              {() => {
                const selectedType = documentTypes.find(t => t.key === participationForm.getFieldValue('identificationTypeId'));
                return (
                  <Form.Item
                    name="identificationNumber"
                    rules={[
                      { required: true, message: intl.formatMessage({ id: 'Field is required' }) },
                      {
                        validator: (_, value) => {
                          if (!value || !selectedType?.pattern) return Promise.resolve();
                          const regex = new RegExp(selectedType.pattern);
                          if (!regex.test(value)) {
                            return Promise.reject(
                              intl.formatMessage({ id: 'Invalid identification number' }, { example: selectedType.example })
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={selectedType?.example || intl.formatMessage({ id: 'Identification number' })}
                      maxLength={20}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>
        </div>

        <div className="cs_form_row two_columns">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Department" /></p>
            <Form.Item name="state" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
              <Select
                className={selectClassName}
                suffixIcon={<ArrowDropDown />}
                placeholder={intl.formatMessage({ id: 'Select department' })}
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                {departments && departments.map(dept => (
                  <Select.Option key={dept.id} value={dept.description}>{dept.description}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Has VISA" /></p>
            <Form.Item name="hasVisa" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }]}>
              <Select
                className={selectClassName}
                suffixIcon={<ArrowDropDown />}
                placeholder={intl.formatMessage({ id: 'Select an option' })}
                dropdownStyle={{ paddingLeft: '0', paddingRight: '0' }}
              >
                <Select.Option value={true}><FormattedMessage id="Yes" /></Select.Option>
                <Select.Option value={false}><FormattedMessage id="No" /></Select.Option>
              </Select>
            </Form.Item>
          </div>
        </div>

        <div className="cs_form_row">
          <div className="cs_field">
            <p className="title"><FormattedMessage id="Address" /></p>
            <Form.Item name="address" rules={[{ required: true, message: intl.formatMessage({ id: 'Field is required' }) }, sanitizeRule(intl.formatMessage({ id: 'Invalid characters detected' }))]}>
              <Input.TextArea placeholder={intl.formatMessage({ id: 'Address' })} rows={3} />
            </Form.Item>
          </div>
        </div>
      </div>

      {isModal ? (
        <div className="side_btns_camp">
          <div className="btn_wrapper">
            <Button className="back_btn" onClick={handleBackToCode}>
              <FormattedMessage id="Cancel" />
            </Button>
            <Button className="save_sar_btn" htmlType="submit" loading={isSaving}>
              <FormattedMessage id="Save" />
              <ArrowForward />
            </Button>
          </div>
        </div>
      ) : (
        <div className="cs_form_actions">
          <Button className="save_btn" htmlType="submit" loading={isSaving}>
            <FormattedMessage id="Save" />
            <ArrowForward />
          </Button>
          <Button className="cancel_btn" onClick={handleBackToCode}>
            <FormattedMessage id="Cancel" />
          </Button>
        </div>
      )}
    </Form>
  );

  const hasMultipleCampaigns = campaigns.length > 1;

  const getStepClass = (targetStep: StepType) => {
    const stepOrder: StepType[] = hasMultipleCampaigns
      ? ['campaign', 'code', 'verify', 'participation']
      : ['code', 'verify', 'participation'];
    const currentIndex = stepOrder.indexOf(step);
    const targetIndex = stepOrder.indexOf(targetStep);
    if (targetIndex === currentIndex) return 'active';
    if (targetIndex < currentIndex) return 'completed';
    return '';
  };

  const getStepNumber = (base: number) => hasMultipleCampaigns ? base + 1 : base;

  const renderContent = () => (
    <div className="cs_modal_content">
      <div className="cs_step_indicator">
        {hasMultipleCampaigns && (
          <>
            <div className={`cs_step ${getStepClass('campaign')}`}>
              <span className="step_number">1</span>
              <span className="step_label"><FormattedMessage id="Campaign" /></span>
            </div>
            <div className="cs_step_divider" />
          </>
        )}
        <div className={`cs_step ${getStepClass('code')}`}>
          <span className="step_number">{getStepNumber(1)}</span>
          <span className="step_label"><FormattedMessage id="Code" /></span>
        </div>
        <div className="cs_step_divider" />
        <div className={`cs_step ${getStepClass('verify')}`}>
          <span className="step_number">{getStepNumber(2)}</span>
          <span className="step_label"><FormattedMessage id="Verification" /></span>
        </div>
        <div className="cs_step_divider" />
        <div className={`cs_step ${getStepClass('participation')}`}>
          <span className="step_number">{getStepNumber(3)}</span>
          <span className="step_label"><FormattedMessage id="Registration" /></span>
        </div>
      </div>
      {validatedCode && step !== 'code' && step !== 'campaign' && (
        <div className="cs_code_badge">
          <span className="cs_code_badge_label"><FormattedMessage id="Code" />:</span>
          <span className="cs_code_badge_value">{validatedCode}</span>
        </div>
      )}
      {step === 'campaign' && renderCampaignStep()}
      {step === 'code' && renderCodeStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'participation' && renderParticipationStep()}
    </div>
  );

  if (isModal) {
    return (
      <ModalDlg className="customer_support_modal" visible={isOpenModal ?? false} width={700} handleCancel={onClose} isCenter>
        <div className="csm_header">
          <div className="csm_header_icon"><ContactSupport /></div>
          <div className="csm_header_title">
            <h3><FormattedMessage id="Add participation" /></h3>
          </div>
          <div className="csm_close" onClick={onClose}><Close /></div>
        </div>
        <div className="cs_form_wrapper">
          {renderContent()}
        </div>
      </ModalDlg>
    );
  }

  if (!isOpenModal) return null;

  return (
    <div className="cs_form_fullscreen">
      <div className="cs_form_wrapper">
        <div className="cs_form_header">
          <h3><FormattedMessage id="Add participation" /></h3>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default CustomerSupportModal;
